// utils/orderBoxItemDisplay.js
//
// Pure display helpers shared by the order-box-items surfaces (ItemCard,
// ItemSheets, BoxTotalsBreakdown). Reads plain props, so it works identically
// on a pinia-orm OrderBoxItem instance and on a plain object (tests, raw
// deserialized payloads).
//
// MANUAL (off-catalog) lines are admin-added items with no catalog
// variant/product — e.g. a delivery fee or an ad-hoc line. Their catalog ids
// and avatar URLs are all null; the display name lives in `manualLabel`.
// `isManual` is the RELIABLE discriminator (backend sync brief
// 2026-07-09-order-box-items-manual-item-fields) — never infer "manual" from
// `variantId == null`.

/**
 * @param {Object|null|undefined} item
 * @returns {boolean} true for a manual (off-catalog) line item.
 */
export function isManualItem(item) {
  return Boolean(item?.isManual);
}

/**
 * The line's display name. Manual lines use `manualLabel` (the backend also
 * mirrors it into variantName/productName for backwards-compat, so those are
 * safe fallbacks). Catalog lines use the product name, unchanged.
 *
 * @param {Object|null|undefined} item
 * @returns {string}
 */
export function itemDisplayName(item) {
  if (!item) return '';
  if (item.isManual) {
    return item.manualLabel || item.variantName || item.productName || '';
  }
  return item.productName ?? '';
}

/**
 * The line's NET total in cents — what the member actually pays for the line.
 * Prefers the backend-computed net `amount` (= qty*price − discount); falls back
 * to gross price*qty for older payloads that predate the `amount` field (sync
 * brief 2026-07-11-orderbox-net-amount-and-ispoola). For a normal line discount
 * is 0, so `amount === price*qty`; for a Poola line `amount` is the cash paid,
 * with the PoolShare-funded discount removed — never the full retail price.
 *
 * @param {Object|null|undefined} item
 * @returns {number} net line total in cents
 */
export function lineNetCents(item) {
  if (!item) return 0;
  if (typeof item.amount === 'number') return item.amount;
  return (item.price ?? 0) * (item.qty ?? 0);
}
